import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangle, Search } from 'lucide-react';

import common from '@/shared/i18n/en/common';
import messages from '@/shared/i18n/en/shell';
import { useT } from '@/shared/i18n/react';
import { term } from '@/shared/lib/glossary';

/**
 * Privacy-safe "not found" / "no access" surface.
 *
 * The page deliberately conflates 404 (the row doesn't exist) and 403 (it
 * does, but you're not allowed) into one body — see docs/22 §7.  Without
 * this conflation, anyone could probe for a task's existence by URL: a
 * 403 says "this is real, but locked"; a 404 says "this is gone".  Both
 * leaks team structure to outsiders.  So the copy is intentionally vague.
 *
 * Reachable via:
 *   - Direct navigation to `/access-denied` (legacy alias kept for any
 *     copy-pasted links from earlier builds).
 *   - The TanStack Router `notFoundComponent` on `__root` for any path
 *     that doesn't match a registered route.
 *   - Programmatic redirects from feature pages that catch a 403/404
 *     response (e.g. `TaskRoutePage` when by-key returns 404).
 */
export function NotFoundPage() {
  const t = useT(messages);
  return (
    <div className="h-full grid place-items-center px-4">
      <div className="max-w-md text-center">
        <div className="size-12 mx-auto mb-4 grid place-items-center rounded-full bg-[var(--bg-muted)]">
          <Search className="size-5 text-[var(--fg-muted)]" />
        </div>
        <h1 className="text-lg font-semibold text-[var(--fg)] mb-2">
          {t('notFoundTitle')}
        </h1>
        <p className="text-sm text-[var(--fg-muted)] mb-6 leading-relaxed">
          {t('notFoundBody')}
        </p>
        <div className="flex items-center gap-2 justify-center">
          <Link
            to="/"
            className="inline-flex items-center h-9 px-4 rounded-md bg-[var(--accent)] text-[var(--accent-fg)] text-sm font-medium hover:opacity-90"
          >
            {t('goToHome')}
          </Link>
          <Link
            to="/my/work"
            className="inline-flex items-center h-9 px-4 rounded-md border border-[var(--border)] text-sm hover:bg-[var(--bg-muted)]"
          >
            {term('myWork')}
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Runtime-error fallback for the TanStack Router `errorComponent`.
 *
 * Renders when a route loader / component throws unrecoverably (network
 * blip, 500 from API, malformed projection).  Stays calm — the user
 * sees "something went wrong" with a reset path, not a stack trace.
 * Sentry sees the full error (wired via the global handler).
 *
 * `error` is passed by TanStack at render time; we surface its message
 * in a collapsible details block so support emails can include it
 * without exposing it to the casual user.
 */
export function RuntimeErrorPage({
  error,
  reset,
}: {
  error: Error;
  reset?: () => void;
}) {
  const t = useT(messages);
  const tc = useT(common);
  return (
    <div className="h-full grid place-items-center px-4">
      <div className="max-w-md text-center">
        <div className="size-12 mx-auto mb-4 grid place-items-center rounded-full bg-[var(--danger-bg)] text-[var(--danger-fg)]">
          <AlertTriangle className="size-5" />
        </div>
        <h1 className="text-lg font-semibold text-[var(--fg)] mb-2">
          {t('errorTitle')}
        </h1>
        <p className="text-sm text-[var(--fg-muted)] mb-6 leading-relaxed">
          {t('errorBody')}
        </p>
        <div className="flex items-center gap-2 justify-center">
          {reset && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center h-9 px-4 rounded-md bg-[var(--accent)] text-[var(--accent-fg)] text-sm font-medium hover:opacity-90"
            >
              {tc('tryAgain')}
            </button>
          )}
          <Link
            to="/"
            className="inline-flex items-center h-9 px-4 rounded-md border border-[var(--border)] text-sm hover:bg-[var(--bg-muted)]"
          >
            {t('goHome')}
          </Link>
        </div>
        {error?.message && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-[var(--fg-muted)] cursor-pointer hover:text-[var(--fg-subtle)]">
              {t('technicalDetails')}
            </summary>
            <pre className="mt-2 text-xs text-[var(--fg-muted)] bg-[var(--bg-muted)] rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words">
              {error.message}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/access-denied')({ component: NotFoundPage });
